/**
 * RequestPayoutModal Component
 *
 * Modal for requesting a payout.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { PayoutMethod, WhishMoneyDetails, WireTransferDetails } from '../hooks';

interface RequestPayoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequest: (payoutMethodId: string) => Promise<void>;
  isRequesting: boolean;
  availableAmount: number;
  payoutMethods: PayoutMethod[];
}

export const RequestPayoutModal: React.FC<RequestPayoutModalProps> = ({
  isOpen,
  onClose,
  onRequest,
  isRequesting,
  availableAmount,
  payoutMethods,
}) => {
  const defaultMethod = payoutMethods.find((m) => m.isDefault);
  const [selectedMethodId, setSelectedMethodId] = useState(defaultMethod?.id || '');

  if (!isOpen) return null;

  const selectedMethod = payoutMethods.find((m) => m.id === selectedMethodId);
  const isWireTransfer = selectedMethod?.type === 'wire_transfer';
  const transferFee = isWireTransfer ? 15 : 0;
  const netAmount = availableAmount - transferFee;
  const canRequest = availableAmount >= 50 && selectedMethod;

  const handleRequest = async () => {
    if (!canRequest) return;
    await onRequest(selectedMethodId);
    onClose();
  };

  const getMethodDisplay = (method: PayoutMethod) => {
    if (method.type === 'whish_money') {
      const details = method.details as WhishMoneyDetails;
      return details.phoneNumber;
    }
    const details = method.details as WireTransferDetails;
    return `${details.bankName} - ${details.accountNumber}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Request Payout</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Amount Summary */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Available amount</span>
              <span className="font-medium text-gray-900">
                ${availableAmount.toFixed(2)}
              </span>
            </div>
            {transferFee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Wire transfer fee</span>
                <span className="text-red-600">-${transferFee.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t">
              <span className="font-medium text-gray-900">You'll receive</span>
              <span className="font-bold text-green-600 text-lg">
                ${netAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Minimum Warning */}
        {availableAmount < 50 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <WarningIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  Minimum payout is $50
                </p>
                <p className="text-sm text-amber-600 mt-1">
                  You need ${(50 - availableAmount).toFixed(2)} more to request a payout.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payout Method Selection */}
        {payoutMethods.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-xl mb-6">
            <p className="text-gray-600 mb-3">No payout methods configured</p>
            <Link
              to="/host/earnings/payout-methods"
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Add a payout method →
            </Link>
          </div>
        ) : (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select payout method
            </label>
            <div className="space-y-2">
              {payoutMethods.map((method) => (
                <label
                  key={method.id}
                  className={`flex items-center gap-3 p-4 border rounded-xl cursor-pointer transition-colors ${
                    selectedMethodId === method.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="payoutMethod"
                    value={method.id}
                    checked={selectedMethodId === method.id}
                    onChange={() => setSelectedMethodId(method.id)}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {method.type === 'whish_money' ? (
                        <PhoneIcon className="w-5 h-5 text-gray-400" />
                      ) : (
                        <BankIcon className="w-5 h-5 text-gray-400" />
                      )}
                      <span className="font-medium text-gray-900">{method.name}</span>
                      {method.isDefault && (
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {getMethodDisplay(method)}
                    </p>
                  </div>
                  {method.type === 'wire_transfer' && (
                    <span className="text-xs text-gray-500">$15 fee</span>
                  )}
                </label>
              ))}
            </div>
            <Link
              to="/host/earnings/payout-methods"
              className="inline-block mt-3 text-sm text-primary-600 hover:text-primary-700"
            >
              + Add new payout method
            </Link>
          </div>
        )}

        {/* Estimated Arrival */}
        {selectedMethod && (
          <div className="text-sm text-gray-600 mb-6">
            <strong>Estimated arrival:</strong>{' '}
            {selectedMethod.type === 'whish_money'
              ? '1-2 business days'
              : '3-5 business days'}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleRequest}
            disabled={!canRequest || isRequesting}
            className="flex-1 py-3 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRequesting ? 'Requesting...' : 'Request Payout'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Icons
const CloseIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const WarningIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path
      fillRule="evenodd"
      d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z"
      clipRule="evenodd"
    />
  </svg>
);

const PhoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const BankIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

export default RequestPayoutModal;
